import { create } from 'zustand'
import type { Coord, CellResult, ShipType, Direction } from '@fleetduel/shared'

export type AnimationType =
  | 'strike-hit'
  | 'strike-miss'
  | 'torpedo'
  | 'sonar'
  | 'ship-sunk'
  | 'victory'
  | 'defeat'

export interface AnimationEvent {
  type: AnimationType
  /** Primary target cell */
  target: Coord
  /** For torpedo: ordered [cell1, cell2]; for strike: single cell result */
  cells?: CellResult[]
  /** For torpedo direction */
  direction?: Direction
  /** For ship-sunk */
  sunkShip?: ShipType
  /** For sonar */
  sonarCount?: number
  /** Callback when animation finishes — apply deferred store update */
  onComplete: () => void
  /** Whether this animation plays on the player's own board (observer) */
  isOwnBoard?: boolean
}

export type CellAnimPhase = 'pending' | 'animating' | 'done'

export interface AnimationStore {
  queue: AnimationEvent[]
  current: AnimationEvent | null
  phase: number
  inputLocked: boolean
  skipRequested: boolean
  reduceMotion: boolean

  // Persistent sonar badges: coordKey → count
  sonarBadges: Map<string, number>

  enqueue: (event: AnimationEvent) => void
  advancePhase: () => void
  skip: () => void
  resolve: () => void
  setReduceMotion: (on: boolean) => void
  addSonarBadge: (key: string, count: number) => void
  resetAnimations: () => void
}

const LOCKOUT_MS = 500

// Read initial reduceMotion from OS preference or localStorage
function getInitialReduceMotion(): boolean {
  try {
    const stored = localStorage.getItem('fleetduel-reduce-motion')
    if (stored !== null) return stored === 'true'
  } catch {}
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }
  return false
}

export const useAnimationStore = create<AnimationStore>((set, get) => ({
  queue: [],
  current: null,
  phase: 0,
  inputLocked: false,
  skipRequested: false,
  reduceMotion: getInitialReduceMotion(),
  sonarBadges: new Map(),

  enqueue: (event) => {
    const { current } = get()
    if (current) {
      // Queue behind current animation
      set(s => ({ queue: [...s.queue, event], inputLocked: true }))
    } else {
      // Start immediately
      set({ current: event, phase: 0, inputLocked: true, skipRequested: false })
    }
  },

  advancePhase: () => {
    set(s => ({ phase: s.phase + 1 }))
  },

  skip: () => {
    set({ skipRequested: true })
  },

  resolve: () => {
    const { current, queue } = get()
    if (current) {
      current.onComplete()
    }

    if (queue.length > 0) {
      const [next, ...rest] = queue
      set({ current: next, queue: rest, phase: 0, skipRequested: false })
    } else {
      set({ current: null, phase: 0, skipRequested: false })
      // Unlock input after lockout period
      setTimeout(() => {
        set({ inputLocked: false })
      }, LOCKOUT_MS)
    }
  },

  setReduceMotion: (on) => {
    try { localStorage.setItem('fleetduel-reduce-motion', String(on)) } catch {}
    set({ reduceMotion: on })
  },

  addSonarBadge: (key, count) => {
    set(s => {
      const badges = new Map(s.sonarBadges)
      badges.set(key, count)
      return { sonarBadges: badges }
    })
  },

  resetAnimations: () => {
    set({
      queue: [],
      current: null,
      phase: 0,
      inputLocked: false,
      skipRequested: false,
      sonarBadges: new Map(),
    })
  },
}))
